import {
  Dispatch,
  FC,
  SetStateAction,
  useEffect, useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Control, UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';

import ApplicationFeasibility from 'components/ApplicationFeasibility';
import EconomicFeasibility from 'components/EconomicFeasibility';
import { AlkaliProps, BriefProps } from 'utils/types';

import donwloadIcon from '/download.svg';

import PlantSetUpFeasibility from 'components/PlantSetUpFeasibility';
import ChemicalFeasibility from 'components/ChemicalFeasibility';
import TechnicalFeasibility from 'components/TechnicalFeasibility';
import ReportFeasibility from 'components/ReportFeasibility';
import withPrintPDF, { IWithPrintPDFComponentProps } from 'HOC/withPrintPDF';
import { cn } from 'utils/cn';

import { IFeasibilityCheck } from '.';

export interface FeasibilityCheckContentProps {
  tabArr: {
    id: number;
    title: string;
  }[];
  setActiveTabHandler: (id: number) => void;
  activeTab: number;
  setActiveTab: Dispatch<SetStateAction<number>>;
  isDisabledDownload: boolean;
  brief?: BriefProps[];
  filteredData: {
    omyaData: AlkaliProps[];
    productData: AlkaliProps[];
  };
  control: Control<any, any>;
  setValue: UseFormSetValue<IFeasibilityCheck>;
  formWatch: UseFormWatch<IFeasibilityCheck>;
}

const FeasibilityCheckContent: FC<FeasibilityCheckContentProps & IWithPrintPDFComponentProps> = ({
  tabArr,
  setActiveTabHandler,
  activeTab,
  setActiveTab,
  isDisabledDownload,
  brief,
  filteredData,
  printLoading,
  setDocumentTitle,
  handlePrint,
  isPDF,
  control,
  setValue,
  formWatch,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const getGeneratedFileName = useMemo(() => `Feasibility Check – ${t(tabArr.find(tab => tab.id === activeTab)?.title || '')}`, [activeTab]);

  useEffect(() => {
    if (!isPDF) {
      setDocumentTitle(getGeneratedFileName);
    }
  }, [getGeneratedFileName]);

  if (!isPDF) {
    return (
      <div className={cn(
        'max-w-[1700px] mx-auto px-[100px] py-[32px] md:px-[16px] md:pb-4',
        { 'px-[16px] pb-4': isPDF },
      )}
      id="feasibility">
        <div className="mb-8">
          <span
            onClick={() => navigate('/')}
            className="text-secondaryText cursor-pointer"
          >
            {t('home')} /
          </span>{' '}
          <span className="font-medium">{t('feasibilityCheck')}</span>
        </div>
        {isPDF ? (
          <div className="flex justify-center mb-4">
            <div className={'font-bold text-center text-lg'} >
              {t(tabArr.find(tab => tab.id === activeTab)?.title || '')}
            </div>
          </div>
        ) : (
          <div className="flex justify-between mb-6 md:flex-col">
            <div className="flex md:flex-wrap">
              {tabArr.map((tab) => (
                <button
                  onClick={() => setActiveTabHandler(tab.id)}
                  className="mr-4 cursor-pointer md:mr-2 md:mb-2"
                  key={tab.id}
                >
                  <div
                    className={`${
                      tab.id === activeTab ? 'font-bold' : 'text-secondaryText'
                    } text-center mb-1.5 md:text-sm`}
                  >
                    {t(tab.title)}
                  </div>
                  <div
                    className={`${
                      tab.id === activeTab ? 'bg-primary' : 'bg-blue50'
                    } w-[160px] h-[8px] rounded md:w-[83px]`}
                  />
                </button>
              ))}
            </div>
            {isDisabledDownload && !isPDF && (
              <button
                onClick={handlePrint}
                className={`${
                  printLoading ? 'cursor-not-allowed' : 'cursor-pointer'
                } flex items-center  md:mt-3`}
                disabled={printLoading}
              >
                <img src={donwloadIcon} alt="" />
                <div className="font-medium text-primary ml-[8px]">
                  {t('download')} PDF
                </div>
              </button>
            )}
          </div>
        )}
        <div>
          <div className={`${activeTab === 1 ? '' : 'hidden'}`}>
            <div>
              <ApplicationFeasibility
                setValue={setValue}
                formWatch={formWatch}
                control={control}
                briefData={brief ? brief : []}
                setActiveTab={setActiveTab}
              />
            </div>
          </div>
          <div className={`${activeTab === 2 ? '' : 'hidden'}`}>
            <div>
              <EconomicFeasibility
                setValue={setValue}
                formWatch={formWatch}
                control={control}
                data={filteredData.productData}
                omyaData={filteredData.omyaData}
                setActiveTab={setActiveTab}
                isPDF={isPDF}
              />
            </div>
          </div>
          <div className={`${activeTab === 3 ? '' : 'hidden'}`}>
            <div>
              <PlantSetUpFeasibility
                setActiveTab={setActiveTab}
                setValue={setValue}
                formWatch={formWatch}
                isPDF={isPDF}
              />
            </div>
          </div>
          <div className={`${activeTab === 4 ? '' : 'hidden'}`}>
            <div>
              <ChemicalFeasibility
                setActiveTab={setActiveTab}
                setValue={setValue}
                formWatch={formWatch}
                control={control}
                isPDF={isPDF}
              />
            </div>
          </div>
          <div className={`${activeTab === 5 ? '' : 'hidden'}`}>
            <div>
              <TechnicalFeasibility
                setActiveTab={setActiveTab}
                setValue={setValue}
                formWatch={formWatch}
                control={control}
                isPDF={isPDF}
              />
            </div>
          </div>
          <div className={`${activeTab === 6 ? '' : 'hidden'}`}>
            <div>
              <ReportFeasibility
                setActiveTab={setActiveTab}
                briefData={brief ? brief : []}
                formWatch={formWatch}
                isPDF={isPDF}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <table>
      <thead>
        <tr>
          <th className='border-none'>

            <div className='flex justify-center mb-4'>
              <div className={'font-bold text-center text-xl text-black'} >
                {t(tabArr.find(tab => tab.id === activeTab)?.title || '')}
              </div>
            </div>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td className='border-none'>
            <div className={`${activeTab === 1 ? '' : 'hidden'}`}>
              <div>
                <ApplicationFeasibility
                  setValue={setValue}
                  formWatch={formWatch}
                  control={control}
                  briefData={brief ? brief : []}
                  setActiveTab={setActiveTab}
                />
              </div>
            </div>
            <div className={`${activeTab === 2 ? '' : 'hidden'}`}>
              <div>
                <EconomicFeasibility
                  setValue={setValue}
                  formWatch={formWatch}
                  control={control}
                  data={filteredData.productData}
                  omyaData={filteredData.omyaData}
                  setActiveTab={setActiveTab}
                  isPDF={isPDF}
                />
              </div>
            </div>
            <div className={`${activeTab === 3 ? '' : 'hidden'}`}>
              <div>
                <PlantSetUpFeasibility
                  setActiveTab={setActiveTab}
                  setValue={setValue}
                  formWatch={formWatch}
                  isPDF={isPDF}
                />
              </div>
            </div>
            <div className={`${activeTab === 4 ? '' : 'hidden'}`}>
              <div>
                <ChemicalFeasibility
                  setActiveTab={setActiveTab}
                  setValue={setValue}
                  formWatch={formWatch}
                  control={control}
                  isPDF={isPDF}
                />
              </div>
            </div>
            <div className={`${activeTab === 5 ? '' : 'hidden'}`}>
              <div>
                <TechnicalFeasibility
                  setActiveTab={setActiveTab}
                  setValue={setValue}
                  formWatch={formWatch}
                  control={control}
                  isPDF={isPDF}
                />
              </div>
            </div>
            <div className={`${activeTab === 6 ? '' : 'hidden'}`}>
              <div>
                <ReportFeasibility
                  setActiveTab={setActiveTab}
                  briefData={brief ? brief : []}
                  formWatch={formWatch}
                  isPDF={isPDF}
                />
              </div>
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  );
};

export default withPrintPDF(FeasibilityCheckContent);
